import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Dieselbe Trennregel wie in Migration a011 (split_category2), in MikoVault
// app/tag_crud.py (normalize_tag_names) und in MikoVault
// scripts/load_miko_csvs.py (split_category2, der Seed-Loader): nur das Komma
// trennt, Teile werden getrimmt, Leeres faellt weg. Damit verhaelt sich
// eingefuegter Text genauso wie die migrierten Altdaten. Vier Kopien, weil eine
// Migration keinen App-Code importieren darf, der Seed-Loader mit psycopg2
// statt dem ORM spricht und das Frontend kein Python ausfuehrt. Wer hier etwas
// aendert, sieht dort nach.
export const splitTagInput = (raw) =>
  (raw || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

const TagInput = ({ value = [], onChange, suggestions = [] }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(-1);
  // Escape closes the suggestion list without touching the draft text; any
  // further typing (the onChange below) lifts this so the list can reopen.
  const [suggestionsHidden, setSuggestionsHidden] = useState(false);

  const picked = new Set(value.map((tag) => tag.toLowerCase()));

  const addFrom = (raw) => {
    const next = [...value];
    const seen = new Set(picked);
    splitTagInput(raw).forEach((name) => {
      const key = name.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      next.push(name);
    });
    if (next.length !== value.length) onChange(next);
    setDraft('');
  };

  const remove = (name) => onChange(value.filter((tag) => tag !== name));

  const needle = draft.trim().toLowerCase();
  const matches = suggestionsHidden || !needle
    ? []
    : suggestions
        .filter(
          (name) => name.toLowerCase().includes(needle) && !picked.has(name.toLowerCase())
        )
        .slice(0, 8);

  // A stale index would otherwise survive a keystroke and point at whatever
  // suggestion happens to now sit at that position.
  const matchesKey = JSON.stringify(matches);
  useEffect(() => {
    setHighlightIndex(-1);
  }, [matchesKey]);

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      if (matches.length === 0) return;
      event.preventDefault();
      setHighlightIndex((i) => (i + 1) % matches.length);
      return;
    }
    if (event.key === 'ArrowUp') {
      if (matches.length === 0) return;
      event.preventDefault();
      setHighlightIndex((i) => (i <= 0 ? matches.length - 1 : i - 1));
      return;
    }
    if (event.key === 'Escape') {
      if (matches.length === 0) return;
      event.preventDefault();
      setSuggestionsHidden(true);
      return;
    }
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      const highlighted = event.key === 'Enter' ? matches[highlightIndex] : undefined;
      addFrom(highlighted !== undefined ? highlighted : draft);
      return;
    }
    // Backspace loescht den letzten Chip nur, wenn das Feld leer ist -- sonst
    // waere Tippfehler-Korrigieren unmoeglich.
    if (event.key === 'Backspace' && draft === '' && value.length > 0) {
      event.preventDefault();
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1 p-1 border border-primary-300 dark:border-accent-500/40 rounded-md">
        {value.map((name) => (
          <span
            key={name}
            data-testid="tag-chip"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-primary-100 dark:bg-yoru-300/40 text-primary-800 dark:text-primary-100"
          >
            {name}
            {/* The glyph is an SVG, not a text child: a text child would
                land in the chip's textContent and break every test that
                reads chip names via .textContent. The negative margin plus
                padding widens the hit area without moving the chip's layout. */}
            <button
              type="button"
              onClick={() => remove(name)}
              aria-label={t('tags.remove', { name })}
              className="h-4 w-4 shrink-0 -m-0.5 p-0.5 text-primary-500 dark:text-primary-300 hover:text-primary-800 dark:hover:text-primary-100"
            >
              <svg
                viewBox="0 0 10 10"
                aria-hidden="true"
                className="h-full w-full"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              >
                <path d="M1 1l8 8M9 1l-8 8" />
              </svg>
            </button>
          </span>
        ))}
        <input
          type="text"
          data-testid="tag-input-field"
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            setSuggestionsHidden(false);
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => addFrom(draft)}
          placeholder={t('tags.placeholder')}
          className="flex-1 min-w-[8rem] px-1 py-0.5 text-sm bg-transparent focus:outline-none"
        />
      </div>

      {matches.length > 0 && (
        <ul className="mt-1 border border-primary-200 dark:border-accent-500/30 rounded-md divide-y divide-primary-100 dark:divide-accent-500/20">
          {matches.map((name, index) => (
            <li key={name}>
              <button
                type="button"
                data-testid="tag-suggestion"
                onClick={() => addFrom(name)}
                className={`w-full text-left px-2 py-1 text-sm hover:bg-primary-50 dark:hover:bg-yoru-300/30 ${
                  index === highlightIndex ? 'bg-primary-100 dark:bg-yoru-300/40' : ''
                }`}
              >
                {name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TagInput;
